import React from 'react';
import { toast } from '../../toast';
import { UploadIcons } from '../../../icons/icons';
import styles from '../../../pages/questions/upload_file/upload_document_dropbox/upload_document_dropbox.module.css';
import { Button } from '../../components';
import { CloseIcon, UploadIcon } from '../../icons/icons';
import { SecondaryTypography } from '../../typography/typography';

type UploadFileProps = {
  uploadedFile: File[] | null;
  setUploadedFile: (file: File[] | null) => void;
  validTypes?: string[];
  isMultiple?: boolean;
  disabled?: boolean;
};

export const UploadFileComponent = ({
  uploadedFile,
  setUploadedFile,
  validTypes = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'application/msword',
    'video/mp4',
  ],
  isMultiple = false,
  disabled = false,
}: UploadFileProps) => {
  const fileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files && files.length) {
      if (!isMultiple && files.length > 1) {
        toast.error('Only one file allowed', { position: 'bottom-center' });
        return;
      }
      const validFiles = Array.from(files).filter((file) => validateFile(file));
      if (validFiles.length > 0) {
        handleNewFiles(Array.from(files));
      }
    }
  };

  const validateFile = (file: File) => {
    return validTypes.includes(file.type);
  };

  const preventDefault = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const removeFile = (index: number) => {
    if (uploadedFile && uploadedFile.length > 0) {
      const updatedFile = [...uploadedFile];
      updatedFile.splice(index, 1);
      setUploadedFile(updatedFile);
    }
  };

  const truncateFileName = (fileName: string, maxLength: number = 30) => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.slice(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.slice(
      0,
      maxLength - 3 - (extension?.length || 0),
    );
    return `${truncatedName}...${extension}`;
  };

  const renderUploadedFiles = () => {
    if (!uploadedFile) return null;
    return (
      <div className='flex gap-2 flex-wrap max-w-full mt-3 px-2'>
        { uploadedFile.map((file, index) => (
          <div
              key={index}
              className='flex items-center gap-2 bg-gray-50 rounded-md py-1.5 px-3 border border-gray-200 group'
              title={file.name}
          >
            <div className='flex items-center gap-2'>
              <svg
                  className='w-4 h-4 text-gray-400'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={1.5}
                    d='M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z'
                />
              </svg>
              <span className='text-sm text-gray-600'>
                { truncateFileName(file.name, 25) }
              </span>
            </div>
            <button
                onClick={() => removeFile(index)}
                className='ml-1 p-1 rounded-md hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors duration-150'
                aria-label='Remove file'
            >
              <svg
                  className='w-3.5 h-3.5'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>
        )) }
      </div>
    );
  };

  const addFileViaInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length) {
      if (!isMultiple && files.length > 1) {
        toast.error('Only one file allowed', { position: 'bottom-center' });
        return;
      }
      const validFiles = Array.from(files).filter((file) => validateFile(file));
      if (validFiles.length > 0) {
        handleNewFiles(Array.from(files));
      }
    }
  };

  const handleNewFiles = (newFiles: File[]) => {
    const validNewFiles = newFiles.filter(validateFile);
    if (validNewFiles.length === 0) {
      toast.error('No valid files selected.', { position: 'bottom-center' });
      return;
    }

    const uniqueNewFiles = validNewFiles.filter(
      (newFile, index, self) =>
        index ===
        self.findIndex(
          (f) => f.name === newFile.name && f.size === newFile.size,
        ),
    );

    const nonDuplicateFiles = uploadedFile
      ? uniqueNewFiles.filter(
        (newFile) =>
          !uploadedFile.some(
            (existingFile) =>
              existingFile.name === newFile.name &&
                existingFile.size === newFile.size,
          ),
      )
      : uniqueNewFiles;

    if (!isMultiple) {
      if (uploadedFile && uploadedFile.length > 0) {
        toast.error('Only one file allowed', { position: 'bottom-center' });
        return;
      }
      setUploadedFile([nonDuplicateFiles[0]]);
    } else {
      const updatedFiles = uploadedFile
        ? [...uploadedFile, ...nonDuplicateFiles]
        : nonDuplicateFiles;
      setUploadedFile(updatedFiles);
    }
  };

  return (
    <div>
      <input
          type='file'
          onChange={addFileViaInput}
          id='upload-file-input'
          style={{ display: 'none' }}
          multiple={isMultiple}
      />
      <div
          className={`
            relative flex flex-col items-center justify-center w-full p-6 bg-white
            border border-dashed rounded-lg transition-all duration-200
            ${
              disabled
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                : 'border-gray-300 hover:border-blue-500 cursor-pointer group'
            }
          `}
          onDragOver={preventDefault}
          onDragEnter={preventDefault}
          onDragLeave={preventDefault}
          onDrop={fileDrop}
      >
        <div className='mb-3'>
          <div className='w-16 h-16 rounded-full bg-blue-100/80 flex items-center justify-center'>
            <UploadIcons className='text-blue-500' />
          </div>
        </div>
        <div className='text-center'>
          <div
              className={`text-sm ${
                disabled ? 'text-gray-400' : 'text-gray-600'
              } mb-1`}
          >
            Drag & Drop your files here - PDF or DOCX
          </div>
          <div
              className={`text-xs ${
                disabled ? 'text-gray-400' : 'text-gray-500'
              } mb-3`}
          >
            or
          </div>
          <button
              onClick={() =>
                !disabled && document.getElementById('upload-file-input')?.click()
              }
              className={`
              inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md
              transition-all duration-200 mb-3
              ${
                disabled
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-blue-500 hover:text-blue-500'
              }
            `}
              disabled={disabled}
          >
            Browse Files
          </button>
          <div className='text-xs text-gray-400'>
            Accepted file types: PDF, DOCX
          </div>
        </div>
      </div>
      <div className='flex pb-2 gap-2'> { renderUploadedFiles() }</div>
    </div>
  );
};
